import React, { useEffect, useState } from "react";
import _ from "lodash";
import moment from "moment";
import styled, { keyframes } from "styled-components";
import { BookingData, ICustomerDetail, IFareCart } from "@interfaces";
import { StyledBorderButton, StyledButton } from "../StyledComponents";
import { breakpoint, theme, useWindowDimensions } from "@utilities";
import { selectAccount, selectRedeemer, useAppSelector } from "@redux";
import { externalApi } from "@api";
import { AddTravellerModal } from "./AddTravellerModal";
import { AddTravellerInformation } from "./AddTravellerInformation";
import { selectQuotes } from "src/Redux/Slices/quotes";
import { useLocation, useParams } from "react-router-dom";
import { PATH_QUOTES } from "@routes";

export interface Suggestion {
    first_name: string | null;
    last_name: string | null;
    phone_number: number;
    country_code: string;
    email: string;
    id: string;
}

interface AddTravellerComponentProps {
    item: IFareCart;
    quantity: number;
    confirmedTravellers: { [date: string]: Set<string> };
    setConfirmedTravellers: React.Dispatch<React.SetStateAction<{ [date: string]: Set<string> }>>;
    onTravellerDataChange?: (data: { [key: number]: Suggestion }) => void;
    onPickupLocationChange: (
        index: number,
        value: { pickupId: string | number; pickupLocation: string; pickupTime: string },
    ) => void;
    onTimeChange: (
        index: number,
        value: { commences: string; bookingTimeId: string | number },
    ) => void;
}

interface StyledSuggestionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isSuggestionClicked: boolean;
    disabled?: boolean;
}
const getDateKey = (booking_date: any) =>
    typeof booking_date === "string" ? booking_date : booking_date.toISOString();

export const AddTravellerComponent = ({
    item,
    quantity,
    confirmedTravellers,
    setConfirmedTravellers,
    onTravellerDataChange,
    onPickupLocationChange,
    onTimeChange,
}: AddTravellerComponentProps) => {
    const formattedDate = moment(item.booking_date).format("MMM D");
    const { account } = useAppSelector(selectAccount);
    const cartCustomers = useAppSelector(selectRedeemer);
    const quotesCustomersData = useAppSelector(selectQuotes);
    const { quoteId: quoteIdFromParam } = useParams<{ quoteId: string }>();
    const quoteId = quoteIdFromParam!;
    const quoteCustomers = _.get(quotesCustomersData, ["quotesCustomers", quoteId]);
    const location = useLocation();
    const currentPath = location.pathname;
    const isQuotesPage = currentPath.includes(PATH_QUOTES);
    const redeemers = isQuotesPage ? quoteCustomers?.customers : cartCustomers.redeemer;
    const [countriesData, setCountriesData] = useState<any[]>([]);
    const [selectedSuggestions, setSelectedSuggestions] = useState<{
        [key: number]: Suggestion | null;
    }>({});
    const [confirmedSelections, setConfirmedSelections] = useState<{ [key: number]: Suggestion }>(
        {},
    );
    const [activeTravellerIndex, setActiveTravellerIndex] = useState<number | null>(null);
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState<boolean>(false);
    const [isTravellerPopupOpen, setIsTravellerPopupOpen] = useState<boolean>(false);
    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const { width } = useWindowDimensions();

    const onAddToSuggestions = (removedTraveller: Suggestion) => {
        setSelectedSuggestions((prevSelectedSuggestions) => {
            const updatedSuggestions = { ...prevSelectedSuggestions };
            const index = _.keys(updatedSuggestions).find(
                (key) => updatedSuggestions[Number(key)]?.id === removedTraveller.id,
            );
            if (!index) {
                updatedSuggestions[_.keys(updatedSuggestions).length] = removedTraveller;
            }
            return updatedSuggestions;
        });
    };
    const dateKey = getDateKey(item.booking_date);

    useEffect(() => {
        document.body.style.overflow = isBottomSheetOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isBottomSheetOpen]);

    useEffect(() => {
        if (width < 768 && (isBottomSheetOpen || isTravellerPopupOpen)) {
            document.body.classList.add("hide-beacon-head");
        } else {
            document.body.classList.remove("hide-beacon-head");
        }

        return () => document.body.classList.remove("hide-beacon-head");
    }, [isBottomSheetOpen, isTravellerPopupOpen, width]);

    useEffect(() => {
        if (onTravellerDataChange) {
            onTravellerDataChange(confirmedSelections);
        }
    }, [confirmedSelections]);

    useEffect(() => {
        if (!_.isEmpty(item.booking_data) && !_.isEmpty(redeemers)) {
            const prefilled: { [key: number]: Suggestion } = {};
            const updatedSet = new Set<string>();

            _.forEach(item.booking_data, (booking: BookingData) => {
                const quantityIndex = booking.quantityIndex;
                const rawRedeemer = _.get(booking, "redeemers[0]", null);
                const redeemerId =
                    typeof rawRedeemer === "object" ? _.get(rawRedeemer, "id") : rawRedeemer;
                const match = _.find(
                    redeemers as ICustomerDetail[],
                    (r) => String(r.id) === String(redeemerId),
                );

                if (match) {
                    prefilled[quantityIndex - 1] = {
                        id: match.id,
                        first_name: match.first_name,
                        last_name: match.last_name,
                        phone_number: Number(match.phone_number),
                        country_code: match.country_code,
                        email: match.email,
                    };
                    updatedSet.add(match.id);
                }
            });

            setConfirmedSelections(prefilled);
            setConfirmedTravellers((prev) => ({
                ...prev,
                [dateKey]: updatedSet,
            }));
        }
    }, [item.booking_data, redeemers, dateKey, setConfirmedTravellers]);

    useEffect(() => {
        (async () => {
            const res = await externalApi.getCountriesDate();
            setCountriesData(res.data);
        })();
    }, []);

    const getCountryName = (targetCode: string | undefined) =>
        _.get(_.find(countriesData, { countryCode: targetCode }), "name", "Not Found");

    const handleSuggestionClick = (suggestion: Suggestion) => {
        if (activeTravellerIndex !== null) {
            setSelectedSuggestions((prev) => ({
                ...prev,
                [activeTravellerIndex]:
                    _.get(prev, activeTravellerIndex)?.id === suggestion.id ? null : suggestion,
            }));
        }
    };

    const handleAddToBookingClick = () => {
        setConfirmedTravellers((prev) => {
            const updatedTravellers = new Set(prev[dateKey] || []);
            _.forEach(selectedSuggestions, (suggestion) => {
                if (suggestion) {
                    updatedTravellers.add(suggestion.id);
                }
            });
            return { ...prev, [dateKey]: updatedTravellers };
        });

        setConfirmedSelections((prev) => {
            const newConfirmed = _.clone(prev);
            _.forEach(selectedSuggestions, (suggestion, key) => {
                if (suggestion) {
                    const fullName = `${suggestion.first_name} ${suggestion.last_name}`;
                    if (newConfirmed[Number(key)]) {
                        const existingNames = _.uniq(
                            (newConfirmed[Number(key)].first_name ?? "").split(", "),
                        );
                        if (!existingNames.includes(fullName)) {
                            newConfirmed[Number(key)].first_name = [
                                ...existingNames,
                                fullName,
                            ].join(", ");
                        }
                    } else {
                        newConfirmed[Number(key)] = {
                            ...suggestion,
                            first_name: fullName,
                            last_name: "",
                        };
                    }
                }
            });
            return newConfirmed;
        });

        setSelectedSuggestions({});
        setIsBottomSheetOpen(false);
        setIsTravellerPopupOpen(false);
    };

    const handleAddTravellerClick = (index: number) => {
        setActiveTravellerIndex(index);
        setIsBottomSheetOpen(true);
        setIsTravellerPopupOpen(true);
    };

    const handleRemoveTraveller = (index: number) => {
        const traveller = confirmedSelections[index];
        if (!traveller) return;
        setConfirmedSelections((prev) => _.omit(prev, index));
        setConfirmedTravellers((prev) => {
            const updatedSet = new Set(prev[dateKey] || []);
            updatedSet.delete(traveller.id);
            return { ...prev, [dateKey]: updatedSet };
        });
        onAddToSuggestions(traveller);
    };

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);

    const isAccountIncomplete = !(
        account?.first_name &&
        account?.last_name &&
        account?.email &&
        account?.phone_number &&
        account?.post_code &&
        account?.country_code
    );

    const filteredSuggestions = _.chain(redeemers as ICustomerDetail[])
        .filter((user) => !!user && !!user.id)
        .filter((user) => !_.get(confirmedTravellers, [dateKey])?.has(user.id))
        .sortBy((user) => -(user.is_primary ?? 0))
        .value();

    const prefilledPickupLocations: {
        [index: number]: { pickupId: string; pickupLocation: string; pickupTime: string };
    } = {};

    _.forEach(item.booking_data, (booking) => {
        if (booking.pickupId && booking.pickupLocation) {
            prefilledPickupLocations[booking.quantityIndex - 1] = {
                pickupId: booking.pickupId,
                pickupLocation: booking.pickupLocation,
                pickupTime: booking.pickupTime,
            };
        }
    });

    const prefilledTime: {
        [index: number]: { commences: string; bookingTimeId: string };
    } = {};

    _.forEach(item.booking_data, (booking) => {
        if (booking.commences && booking.timeId) {
            prefilledTime[booking.quantityIndex - 1] = {
                bookingTimeId: booking.timeId,
                commences: booking.commences,
            };
        }
    });

    return (
        <Container>
            <Date>{`(${formattedDate})`}</Date>
            <h2 className="quantity">
                Quantity : {quantity} <span>(Who are staying here?)</span>
            </h2>

            {_.range(quantity).map((index) => (
                <AddTravellerInformation
                    key={index}
                    index={index}
                    traveller={confirmedSelections[index] || null}
                    onAddTraveller={() => handleAddTravellerClick(index)}
                    onRemoveTraveller={() => handleRemoveTraveller(index)}
                    onAddTravellerClick={handleAddTravellerClick}
                    bookingItem={item}
                    onPickupLocationChange={(value) => onPickupLocationChange(index, value)}
                    onTimeChange={(value) => onTimeChange(index, value)}
                    prefilledPickupLocation={prefilledPickupLocations[index]}
                    prefilledTime={prefilledTime[index]}
                    onAddToSuggestions={onAddToSuggestions} // Pass the function as a prop
                />
            ))}

            {isBottomSheetOpen && (
                <>
                    <Overlay onClick={() => setIsBottomSheetOpen(false)} />
                    <BottomSheet onClick={(e) => e.stopPropagation()}>
                        {isAccountIncomplete ? (
                            <div className="text-container">
                                <h2>Your Personal details are missing</h2>
                                <p>
                                    You haven't entered your personal details yet. Please fill them
                                    in to proceed.
                                </p>
                                <StyledButton
                                    $borderRadius="4px"
                                    color={["#436CB2 0%", "#28B7C3 100%"]}
                                >
                                    Add my personal details
                                </StyledButton>
                            </div>
                        ) : (
                            <>
                                <div className="details-container">
                                    <h2>Suggestions</h2>
                                    {_.map(filteredSuggestions, (user) => (
                                        <div className="details" key={user.id}>
                                            <span>
                                                <StyledSuggestionButton
                                                    type="button"
                                                    isSuggestionClicked={
                                                        activeTravellerIndex !== null &&
                                                        _.get(
                                                            selectedSuggestions,
                                                            activeTravellerIndex,
                                                        )?.id === user.id
                                                    }
                                                    onClick={() =>
                                                        handleSuggestionClick({
                                                            first_name: user.first_name,
                                                            last_name: user.last_name,
                                                            phone_number: Number(user.phone_number),
                                                            country_code: user.country_code,
                                                            email: user.email,
                                                            id: user.id,
                                                        })
                                                    }
                                                >
                                                    <div />
                                                </StyledSuggestionButton>
                                            </span>
                                            <div>
                                                <h1>
                                                    {user.first_name} {user.last_name}{" "}
                                                    {user.is_primary === 1 && "(you)"}
                                                </h1>
                                                <p>
                                                    {user.phone_number},{" "}
                                                    {getCountryName(user.country_code)},{" "}
                                                    {user.email}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="styled-buttons">
                                    <BorderButton
                                        onClick={handleOpenModal}
                                        $borderRadius="4px"
                                        color="transparent"
                                        $textColor={theme.colors.main}
                                    >
                                        Create New
                                    </BorderButton>
                                    <NormalStyledButton
                                        $borderRadius="4px"
                                        color={["#436CB2 0%", "#28B7C3 100%"]}
                                        onClick={handleAddToBookingClick}
                                    >
                                        Add to booking
                                    </NormalStyledButton>
                                </div>
                            </>
                        )}
                    </BottomSheet>
                </>
            )}
            {isTravellerPopupOpen && (
                <TravellerPopup onClick={() => setIsTravellerPopupOpen(false)}>
                    <Popup onClick={(e) => e.stopPropagation()}>
                        {isAccountIncomplete ? (
                            <PopupContent>
                                <h2>
                                    Your personal details <br /> are missing
                                </h2>
                                <p>
                                    You haven't entered your personal details yet.
                                    <br />
                                    <span style={{ display: "block", marginTop: "10px" }}>
                                        Please fill them in to proceed.
                                    </span>
                                </p>
                                <div className="styled-buttons">
                                    <StyledButton
                                        $borderRadius="4px"
                                        color={["#436CB2 0%", "#28B7C3 100%"]}
                                    >
                                        Add my personal details
                                    </StyledButton>
                                    <BorderButton
                                        onClick={() => setIsTravellerPopupOpen(false)}
                                        $borderRadius="4px"
                                        color="transparent"
                                        $textColor={theme.colors.main}
                                    >
                                        Cancel
                                    </BorderButton>
                                </div>
                            </PopupContent>
                        ) : (
                            <SuggesstionPopup>
                                <div className="details-container-web">
                                    <h2>Suggestions</h2>
                                    {_.map(filteredSuggestions, (user) => (
                                        <div className="details" key={user.id}>
                                            <span>
                                                <StyledSuggestionButton
                                                    type="button"
                                                    isSuggestionClicked={
                                                        activeTravellerIndex !== null &&
                                                        _.get(
                                                            selectedSuggestions,
                                                            activeTravellerIndex,
                                                        )?.id === user.id
                                                    }
                                                    onClick={() =>
                                                        handleSuggestionClick({
                                                            first_name: user.first_name,
                                                            last_name: user.last_name,
                                                            phone_number: Number(user.phone_number),
                                                            country_code: user.country_code,
                                                            email: user.email,
                                                            id: user.id,
                                                        })
                                                    }
                                                >
                                                    <div />
                                                </StyledSuggestionButton>
                                            </span>
                                            <div>
                                                <h1>
                                                    {user.first_name} {user.last_name}{" "}
                                                    {user.is_primary === 1 && "(you)"}
                                                </h1>
                                                <p>
                                                    {user.phone_number},{" "}
                                                    {getCountryName(user.country_code)},{" "}
                                                    {user.email}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="styled-buttons">
                                    <BorderButton
                                        onClick={handleOpenModal}
                                        $borderRadius="4px"
                                        color="transparent"
                                        $textColor={theme.colors.main}
                                    >
                                        Create New
                                    </BorderButton>
                                    <NormalStyledButton
                                        $borderRadius="4px"
                                        color={["#436CB2 0%", "#28B7C3 100%"]}
                                        onClick={handleAddToBookingClick}
                                    >
                                        Add to booking
                                    </NormalStyledButton>
                                </div>
                            </SuggesstionPopup>
                        )}
                    </Popup>
                </TravellerPopup>
            )}

            <AddTravellerModal
                countriesData={countriesData}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                isQuotesPage={isQuotesPage}
                quoteId={quoteId}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    .quantity {
        font-family: Plus Jakarta Sans;
        font-size: 18px;
        font-weight: 600;
        span {
            color: #3c3c3c;
            font-size: 12px;
            font-weight: 400;
        }
    }

    .travellerContainer {
        display: flex;
        gap: 10px;
        align-items: center;

        .listCount {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            background-color: #f4f6f7;
            border-radius: 50px;
            font-weight: 600;
        }
    }
`;

const Date = styled.p`
    font-size: 12px;
    font-weight: 600;
    font-family: Plus Jakarta Sans;
    color: #232b2b;
    margin: 4px 0px 8px 0px;
    text-decoration: underline;
    text-underline-offset: 3px;
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const BorderButton = styled(StyledBorderButton)`
    margin-top: 0px;
    padding: 24px 0px;
`;

const NormalStyledButton = styled(StyledButton)`
    margin-top: 0px;
    padding: 25px 0px;
`;

const StyledSuggestionButton = styled.button<StyledSuggestionButtonProps>`
    width: 24px;
    height: 24px;
    padding: 6px;
    border-radius: 50px;
    border: ${(props) => (props.isSuggestionClicked ? "1px solid #7fbfff" : "1px solid #daddde")};
    transition: background-color 0.3s ease;
    cursor: pointer;
    background-color: ${(props) => (props.isSuggestionClicked ? "#7fbfff" : "")};

    div {
        width: 100%;
        height: 100%;
        border-radius: 50px;
        background-color: #fff;
    }
`;

const Overlay = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
    display: block;`,
    )}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
    cursor: pointer;
`;

const BottomSheet = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 24px 16px;

        .text-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
            text-align: center;
            margin-bottom: 30px;
            h2 {
                font-size: 20px;
                font-weight: 600;
            }
        }

        .details-container{
            display: flex;
            flex-direction: column;
            gap: 20px;
            margin-bottom: 30px;
            h2 {
                font-size: 20px;
                font-weight: 600;
            }
            .details {
                display : flex;
                align-items : center;
                gap : 16px;
                div{
                    display : flex;
                    flex-direction : column;
                    gap : 6px;
                    h1{
                        font-size : 24px;
                        font-weight : 600;  
                    }
                    p{
                        font-size : 12px;
                        color : #6E7375;
                    }
                }
            }
            
        }
        .styled-buttons {
            display : flex;
            gap : 14px;
        }
    `,
    )}
`;

const TravellerPopup = styled.div`
    ${breakpoint.breakOnlyMobile`
        display: none;
    `}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 15;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Popup = styled.div`
    background: #fff;
    border-radius: 8px;
`;

const PopupContent = styled.div`
    padding: 54px;
    max-width: 90%;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
    text-align: center;
    h2 {
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 22px;
    }
    p {
        color: #6e7375;
        font-size: 16px;
        margin-bottom: 22px;
    }
    .styled-buttons {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }
`;

const SuggesstionPopup = styled.div`
    width: 700px;
    padding: 50px 30px;
    .details-container-web {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-bottom: 30px;
        h2 {
            font-size: 26px;
            font-weight: 700;
        }
        .details {
            display: flex;
            align-items: center;
            gap: 16px;
            div {
                display: flex;
                flex-direction: column;
                gap: 6px;
                h1 {
                    font-size: 24px;
                    font-weight: 600;
                }
                p {
                    font-size: 12px;
                    color: #6e7375;
                }
            }
        }
    }

    .styled-buttons {
        display: flex;
        gap: 24px;
    }
`;
